import { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { UserKeys } from '../../../api/users-api';
import { useUserSettings } from '../../../utils/use-user-settings';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import CreateButton from '../../../shared/buttons/create-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import { useApplyGroupTemplatePermission } from '../group-details/apply-template-dialog';
interface GroupsGridToolbarProps {
  paginationModel: {
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
  };
  setPaginationModel: Dispatch<
    SetStateAction<{
      pageSize: number;
      page: number;
      cursor: string | null;
      search: string;
    }>
  >;
  setOpenAddGroup: Dispatch<SetStateAction<boolean>>;
  setOpenDeleteGroups: Dispatch<SetStateAction<boolean>>;
  setOpenApplyTemplate: Dispatch<SetStateAction<boolean>>;
  selectedRows: string[];
}

export default function GroupsGridToolbar({
  paginationModel,
  setPaginationModel,
  setOpenAddGroup,
  setOpenDeleteGroups,
  setOpenApplyTemplate,
  selectedRows,
}: GroupsGridToolbarProps) {
  const queryClient = useQueryClient();
  const { directoryRoles } = useUserSettings();
  const isAbleToCreateGroup = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.CreateGroup.value
  );
  const { canApplyTemplate, disabledReason: applyTemplateDisabledReason } =
    useApplyGroupTemplatePermission(directoryRoles ?? []);

  return (
    <SharedToolbar
      noFilter
      quickFilterDebounceMs={300}
      primaryButtons={
        <CreateButton
          disabled={!isAbleToCreateGroup}
          onClick={() => setOpenAddGroup(true)}
          label="Add Group"
          tooltipLabel={
            !isAbleToCreateGroup
              ? Messages.Errors.Permission.Acting(
                  DirectoryRoles.CreateGroup.name
                )
              : ''
          }
        />
      }
      secondaryButtons={
        <>
          <SharedIconButton
            variant="applyTemplate"
            onClick={() => setOpenApplyTemplate(true)}
            disabled={selectedRows.length === 0 || !canApplyTemplate}
            tooltipLabel={
              !canApplyTemplate ? applyTemplateDisabledReason : 'Apply Template'
            }
          />
          <SharedIconButton
            variant="delete"
            onClick={() => setOpenDeleteGroups(true)}
            disabled={
              selectedRows.length === 0 ||
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.DeleteGroup.value
              )
            }
            tooltipLabel="Delete"
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              setPaginationModel({
                page: 0,
                pageSize: paginationModel.pageSize,
                cursor: null,
                search: '',
              });
              [
                UserKeys.Groups,
                UserKeys.DirectoryGroup,
                UserKeys.GroupSettings,
                UserKeys.GroupLabels,
              ].forEach((key) => {
                void queryClient.invalidateQueries({ queryKey: [key] });
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="groupsGridStates"
            module={ModuleList.modules.drive.name}
            section={ModuleList.modules.directory.sections.groups}
          />
        </>
      }
    ></SharedToolbar>
  );
}
